import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { useLang } from "@/context/LanguageContext";
import { fmtRM } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, CheckCircle2, Clock, Camera, Loader2 } from "lucide-react";

export default function Handovers() {
  const { t } = useLang();
  const { user } = useAuth();
  const isDirector = user?.role === "director" || user?.role === "manager";
  const [list, setList] = useState([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/handovers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || !file) { toast.error("Amount and photo proof are required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("amount", amount);
      fd.append("note", note);
      fd.append("photo", file);
      await api.post("/handovers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Handover recorded — awaiting Director confirmation");
      setAmount(""); setNote(""); setFile(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setSaving(false); }
  };

  const confirm = async (id) => {
    try {
      await api.post(`/handovers/${id}/confirm`, { note: "" });
      toast.success("Receipt confirmed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none";

  return (
    <Layout>
      <PageHeader title={t("ho.title")} subtitle={t("ho.subtitle")} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 h-fit">
          <h3 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2"><Upload className="h-4 w-4" /> Record Handover</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="handover-form">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Amount (RM)</label>
              <input type="number" step="0.01" data-testid="handover-amount-input" className={`${inputCls} mt-1.5`} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Note</label>
              <input data-testid="handover-note-input" className={`${inputCls} mt-1.5`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. June collections from 5 customers" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Photo Proof</label>
              <input ref={fileRef} type="file" accept="image/*" data-testid="handover-photo-input" onChange={onFile} className="mt-1.5 block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-900 file:text-white hover:file:bg-slate-800 cursor-pointer" />
              {preview && <img src={preview} alt="preview" className="mt-3 rounded-md border border-slate-200 max-h-40 object-cover" />}
            </div>
            <button type="submit" disabled={saving} data-testid="handover-submit-btn" className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Submit Handover
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-3" data-testid="handover-list">
          {list.map((h) => (
            <div key={h.handoverId} data-testid={`handover-${h.handoverId}`} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex items-start gap-4">
              <a href={`${API}/files/${h.photoPath}`} target="_blank" rel="noreferrer" className="shrink-0">
                <img src={`${API}/files/${h.photoPath}`} alt="proof" className="h-20 w-20 rounded-md object-cover border border-slate-200" />
              </a>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono-fig text-lg font-bold text-slate-900">{fmtRM(h.amount)}</span>
                  {h.status === "Confirmed" ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="h-3 w-3" /> Confirmed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3" /> Pending</span>
                  )}
                </div>
                <div className="text-sm text-slate-600 mt-1">{h.note || "—"}</div>
                <div className="text-xs text-slate-400 mt-1">
                  By {h.collectedBy} · {h.createdAt?.slice(0, 10)}
                  {h.status === "Confirmed" && h.confirmedBy && <span> · Confirmed by {h.confirmedBy}</span>}
                </div>
                {h.status !== "Confirmed" && isDirector && (
                  <button data-testid={`confirm-${h.handoverId}`} onClick={() => confirm(h.handoverId)} className="mt-2 bg-green-600 text-white hover:bg-green-700 rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Received
                  </button>
                )}
                {h.status !== "Confirmed" && !isDirector && (
                  <div className="mt-2 text-xs text-slate-400 italic">Awaiting Director confirmation</div>
                )}
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400">No handovers yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
